import React, { useState } from 'react';
import { FileText, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getStage, stageBusy } from '../../lib/runHelpers';

export const ShowNotesTab = ({ run, status, onCreate, loading = false, error }) => {
  const [copied, setCopied] = useState(false);
  const llmResponse = run.llmResponse || '';
  const mapping = getStage(run, 'mapping');
  const waiting = stageBusy(mapping) && !['error', 'failed'].includes(status.key);
  const canCreate = onCreate && !['error', 'failed'].includes(status.key);

  const handleCopy = async () => {
    try {
      const tmp = document.createElement('div');
      tmp.innerHTML = llmResponse;
      await navigator.clipboard.writeText(tmp.textContent || '');
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="p-4 sm:p-8">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Show Notes</h2>

      {!llmResponse ? (
        <div className="bg-brand-surface rounded-xl p-8 sm:p-12 border border-brand-surface-3 flex flex-col items-center justify-center text-center">
          {waiting ? (
            <>
              <Loader2 className="w-10 h-10 text-orange-400 animate-spin mb-3" />
              <p className="text-slate-300">{run.transcription?.status !== 'done' && !run.transcriptText ? 'Show notes will start after transcription.' : 'Creating show notes…'}</p>
              <p className="text-slate-500 text-sm mt-1">
                This page updates in real time.
              </p>
            </>
          ) : (
            <>
              <FileText className="w-10 h-10 text-slate-500 mb-3" />
              <p className="text-slate-300">{mapping.status === 'failed' ? 'Show notes could not be created. Your transcript is still available.' : 'No show notes available yet'}</p>
              {(error || mapping.errorMessage) && <p role="alert" className="text-red-400 mt-2">{error || mapping.errorMessage}</p>}
              {canCreate && <button onClick={onCreate} disabled={loading} className="mt-4 px-6 py-3 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white rounded-xl">{loading ? 'Requesting…' : mapping.status === 'failed' || mapping.leaseUntil ? 'Retry Show Notes' : 'Create Show Notes'}</button>}
            </>
          )}
        </div>
      ) : (
        <div className="bg-brand-surface rounded-xl p-4 sm:p-6 border border-brand-surface-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
              Show Notes
            </h3>
            <button
              onClick={handleCopy}
              className="text-slate-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-brand-surface-3"
              title="Copy show notes"
            >
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <div
            dir={run.language === 'heb' ? 'rtl' : 'ltr'}
            className="text-sm sm:text-base text-slate-200 leading-relaxed break-words [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-white [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:ps-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:ps-5 [&_ol]:mb-3 [&_li]:mb-1 [&_strong]:text-white [&_a]:text-orange-400 [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: llmResponse }}
          />
        </div>
      )}
    </div>
  );
};
